import { Box, Button, Stack, Typography } from '@mui/material';

import { endSession, getStoredAdminEmail } from '@/auth';

import theme from '../../theme';

interface StartupErrorProps {
  error: unknown;
}

const describe = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return 'Unknown error';
};

/**
 * Full-screen replacement for the startup loader when the data the app needs to
 * render at all could not be loaded. Nothing else is mounted at that point, so
 * this is the only place the user can retry or log out from.
 */
export const StartupError = ({ error }: StartupErrorProps): JSX.Element => {
  const adminEmail = getStoredAdminEmail();

  return (
    <Box
      data-component-id="STARTUP-ERROR"
      sx={{
        alignItems: 'center',
        backgroundColor: '#343736ff',
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        justifyContent: 'center',
        left: 0,
        position: 'fixed',
        px: 2,
        textAlign: 'center',
        top: 0,
        width: '100vw',
      }}
    >
      <Box sx={{ maxWidth: 600, width: '100%' }}>
        <img src="/logo.png" alt="SimpleSub Water" style={{ width: '100%' }} />
      </Box>
      <Typography variant="h5" sx={{ color: theme.palette.secondary.main, mb: 2 }}>
        We couldn&apos;t load your account
      </Typography>
      {adminEmail && (
        <Typography variant="body1" sx={{ color: 'white', mb: 2, maxWidth: 520 }}>
          You are logged in as support for <strong>{adminEmail}</strong>. If that address does not
          belong to a customer, log out and try again.
        </Typography>
      )}
      <Typography
        variant="body2"
        sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 4, maxWidth: 520, wordBreak: 'break-word' }}
      >
        {describe(error)}
      </Typography>
      <Stack direction="row" spacing={2}>
        <Button variant="outlined" color="secondary" onClick={() => window.location.reload()}>
          Try again
        </Button>
        <Button variant="contained" color="secondary" onClick={() => endSession()}>
          Log out
        </Button>
      </Stack>
    </Box>
  );
};

export default StartupError;
